import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// CORRECCIÓN: Rutas relativas recalibradas de forma precisa a la profundidad de la carpeta Cobranzas
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

// --- HELPERS INTERNOS DE PROCESAMIENTO ---
const getMs = (val) => {
    if (!val) return 0;
    if (val && typeof val === 'object' && 'seconds' in val) {
        return val.seconds * 1000;
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? 0 : d.getTime();
};

const getISODate = (val) => {
    if (!val) return '';
    const date = (val && val.seconds) ? new Date(val.seconds * 1000) : new Date(val);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
};

const getImportePriority = (monto, moneda) => {
    const isUSD = String(moneda || '').toUpperCase() === 'USD';
    if (isUSD || monto > 300000) {
        return { label: 'P1', class: 'bg-red-200 text-red-900 border-red-400', val: 1 };
    }
    if (monto >= 100000) {
        return { label: 'P2', class: 'bg-orange-200 text-orange-900 border-orange-400', val: 2 };
    }
    return { label: 'P3', class: 'bg-yellow-100 text-yellow-900 border-yellow-300', val: 3 };
};

/**
 * useCobranzasTable - Hook de negocio unificado para el Tablero de Cobranzas.
 */
export const useCobranzasTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [deudas, setDeudas] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [showDeepDebtModal, setShowDeepDebtModal] = useState(false);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false);
    const [activeDeuda, setActiveDeuda] = useState(null);
    const [activePolizaData, setActivePolizaData] = useState(null);

    // REFS DE CONCURRENCIA
    const activeItemPathRef = useRef(null);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND (useDataHydration) ---
    const { clientsCache, policiesCache } = useDataHydration(deudas);

    // --- 2. LISTENER EN TIEMPO REAL Y REDUCCIÓN DE CUOTAS EN CALIENTE ---
    useEffect(() => {
        const q = query(collectionGroup(db, 'cobranza'));
        
        const unsubscribe = onSnapshot(q, (snap) => {
            const raw = snap.docs.map(d => ({ 
                id: d.id, 
                path: d.ref.path, 
                clienteId: d.ref.path.split('/')[3],
                polizaId: d.ref.path.split('/')[5],
                ...d.data() 
            }));

            const excludedStates = ['COBRADA', 'ANULADA', 'DEBITADA', 'INCOBRABLE', 'ACREDITADA'];
            const policyGroups = new Map();
            raw.forEach(rec => {
                const key = `${rec.clienteId}_${rec.polizaId}`;
                if (!policyGroups.has(key)) policyGroups.set(key, []);
                policyGroups.get(key).push(rec);
            });

            const processed = Array.from(policyGroups.values()).map(group => {
                const sortedBySequence = [...group].sort((a, b) => {
                    const valA = Number(a.cuota || a.nroRecibo || a.endoso || 0);
                    const valB = Number(b.cuota || b.nroRecibo || b.endoso || 0);
                    if (valA !== valB) return valB - valA;
                    return getMs(b.ultimaModificacion || b.fechaCarga) - getMs(a.ultimaModificacion || a.fechaCarga);
                });
                const unpaid = sortedBySequence.filter(i => !excludedStates.includes((i.estado || '').toUpperCase().trim()));
                if (unpaid.length === 0) return null;
                return { ...unpaid[0], totalCuotas: unpaid.length };
            }).filter(Boolean);

            setDeudas(processed);
            setLoading(false);
        }, (error) => console.error("Error cargando deudas de cobranza:", error));

        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        const polizasConDeudaPorCliente = {}; 
        deudas.forEach(d => {
            const pKey = `${d.clienteId}|${d.polizaId}`;
            const pCache = policiesCache[pKey];
            const numPoliza = d.nPoliza || pCache?.nPoliza || 'S/N';
            if (!polizasConDeudaPorCliente[d.clienteId]) polizasConDeudaPorCliente[d.clienteId] = new Set();
            polizasConDeudaPorCliente[d.clienteId].add(numPoliza);
        });

        return deudas.map(d => {
            const c = clientsCache[d.clienteId] || {};
            const pKey = `${d.clienteId}|${d.polizaId}`;
            const p = policiesCache[pKey] || {};
            
            const currentNPoliza = d.nPoliza || p.nPoliza || 'S/N';
            const todasLasPolizasDeEseCliente = Array.from(polizasConDeudaPorCliente[d.clienteId] || []);
            const otrasPolizas = todasLasPolizasDeEseCliente.filter(num => num !== currentNPoliza && num !== 'S/N');
            
            let displayDocNum = d.clienteCuit || d.clienteDni || '-';
            let displayNameStr = `${d.clienteNombre || ''} ${d.clienteApellido || ''}`.trim();

            if (!displayNameStr && c) {
                displayNameStr = c.persona === 'JURIDICA' ? (c.nombre || 'S/D') : `${c.nombre || ''} ${c.apellido || ''}`.trim();
                displayDocNum = c.cuit || c.dni || '-';
            }
            if (!displayNameStr) displayNameStr = 'S/D';

            const priorityInfo = getImportePriority(d.importe, d.moneda);

            return {
                ...d,
                displayNombre: displayNameStr.toUpperCase(),
                displayDoc: displayDocNum,
                nPoliza: currentNPoliza,
                compania: d.compania || p.compania || 'S/C',
                riesgo: d.riesgo || p.riesgo || 'S/R',
                subriesgo: d.subriesgo || p.subriesgo || '',
                canal: d.canal || p.canal || '-',
                canalNombre: d.canalNombre || p.canalNombre || p.subcanal || '-',
                subcanalNombre: d.subcanal || p.subcanal || '',
                formaPago: d.formaPago || p.formaPago || '-',
                prioridad: priorityInfo.val, // Seteado de forma síncrona para filtros
                estadoGestion: d.estado || 'PENDIENTE', // Seteado de forma síncrona para filtros
                tieneDeudaMultiple: otrasPolizas.length > 0,
                packCount: otrasPolizas.length,
                detalleMultiDeuda: otrasPolizas.length > 0 ? `Este asegurado también posee deudas en: ${otrasPolizas.join(' / ')}` : '',
                
                // Mapeamos ISO Strings de fecha para filtros dinámicos directos
                fechaCarga: getISODate(d.ultimaModificacion || d.fechaCarga),
                fechaReclamo: getISODate(d.fechaReclamo),
                importeDeuda: d.importe // Seteamos para ordenamiento numérico de useTableState
            };
        });
    }, [deudas, clientsCache, policiesCache]);

    // --- 4. PREVENCIÓN ACTIVA DE FUC (Flash of Unhydrated Content) ---
    // Evaluamos si el componente necesita demorar el spinner hasta que se complete la hidratación de caches relacionales
    const needsHydration = useMemo(() => {
        if (deudas.length === 0) return false;
        return deudas.some(d => {
            const hasClient = !!clientsCache[d.clienteId];
            const hasPolicy = !!policiesCache[`${d.clienteId}|${d.polizaId}`];
            return !hasClient || !hasPolicy;
        });
    }, [deudas, clientsCache, policiesCache]);

    // --- 5. HOOK DE CONTROL SÍNCRONO DE TABLAS (useTableState) ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaCarga', direction: 'descending' },
        initialColumns: {
            fechaCarga: true, nPoliza: true, compania: true, displayNombre: true, displayDoc: true, riesgo: true, importeDeuda: true, estadoGestion: true, estadoDeuda: true, intentoCobro: true, formaPago: true, fechaReclamo: true, quienInformo: true, canal: true
        },
        recordsPerPage: 100
    });

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al desbloquear caso de cobranza:", e); }
    }, []);

    // --- 6. INTERCEPTOR DE CONTROLADORES DE ACCIÓN ---
    const handleAction = useCallback(async (item, type) => {
        const pKey = `${item.clienteId}|${item.polizaId}`;
        const pData = policiesCache[pKey] || { id: item.polizaId, nPoliza: item.nPoliza, clienteId: item.clienteId, compania: item.compania, riesgo: item.riesgo };

        setActiveDeuda(item);

        if (type === 'view_debt_deep') {
            setActivePolizaData(pData);
            activeItemPathRef.current = item.path;
            setShowDeepDebtModal(true);
            
            await updateDoc(doc(db, item.path), { 
                bloqueo: { userId: user?.uid, userName: profile?.userName || 'Usuario', timestamp: serverTimestamp() } 
            }).catch(() => { activeItemPathRef.current = null; });
        } else if (type === 'view_policy_detail') {
            setActivePolizaData(pData);
            setShowPolicyModal(true);
        } else if (type === 'view_client_ficha') {
            setShowFichaModal(true);
        }
    }, [policiesCache, user, profile]);

    return {
        loading,
        needsHydration,
        clientsCache,
        activeDeuda,
        activePolizaData,
        showDeepDebtModal,
        showPolicyModal,
        showFichaModal,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDeepDebtModal,
        setShowPolicyModal,
        setShowFichaModal,
        setActiveDeuda,
        setActivePolizaData,
        handleAction,
        unlockPoliza
    };
};